import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Activity,
  Clock,
  Cpu,
  ArrowLeft,
  RefreshCw,
  Mic,
  FileText,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  History,
  GitCompare,
  Brain,
  MessageSquare,
  Hammer,
  GraduationCap,
  HelpCircle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import { analyticsApi } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import UsageHistory from './UsageHistory';
import EditHistory from './EditHistory';
import ClassificationInsights from './ClassificationInsights';
import FeedbackInbox from './FeedbackInbox';
import Workshop from './Workshop';
import CostAnalytics from './CostAnalytics';
import Customers from './Customers';
import Billing from './Billing';
import Learning from './Learning';
import { DollarSign, Users, CreditCard } from 'lucide-react';

interface SummaryData {
  period_days: number;
  transcriptions: {
    total: number;
    errors: number;
    error_rate: number;
    latency: { avg_ms: number; p95_ms: number };
  };
  generations: {
    total: number;
    errors: number;
    error_rate: number;
    latency: { avg_ms: number; p95_ms: number };
  };
}

interface ModelData {
  period_days: number;
  models: Record<string, { total: number; errors: number; avg_latency_ms: number }>;
}

interface DailyData {
  period_days: number;
  daily: Record<string, { transcriptions: number; generations: number }>;
}

interface LogEntry {
  id: string;
  user_id: string;
  event_type: string;
  audio_duration_seconds: number | null;
  transcription_text: string | null;
  whisper_language: string | null;
  model_used: string | null;
  ai_response_text: string | null;
  exam_category: string | null;
  latency_ms: number | null;
  status: string;
  error_message: string | null;
  created_at: string;
}

interface HealthData {
  status: string;
  version: string;
  services: {
    database: { status: string };
    whisper?: { status: string; model_loaded?: boolean; device?: string; model?: string };
    transcription?: { backend: string };
    llm: { default_model: string; openai_configured: boolean; anthropic_configured: boolean };
  };
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'overview' | 'customers' | 'billing' | 'costs' | 'history' | 'edits' | 'classification' | 'learning' | 'feedback' | 'workshop'>('overview');
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [modelData, setModelData] = useState<ModelData | null>(null);
  const [dailyData, setDailyData] = useState<DailyData | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [health, setHealth] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [logFilter, setLogFilter] = useState<string>('');
  const [expandedLog, setExpandedLog] = useState<string | null>(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, m, d, l, h] = await Promise.all([
        analyticsApi.summary(days),
        analyticsApi.byModel(days),
        analyticsApi.daily(days),
        analyticsApi.recent(logFilter || undefined, 100),
        analyticsApi.healthDetailed(),
      ]);
      setSummary(s);
      setModelData(m);
      setDailyData(d);
      setLogs(l);
      setHealth(h);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [days, logFilter]);

  const dailyChartData = dailyData
    ? Object.entries(dailyData.daily)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([day, counts]) => ({
          date: day.slice(5), // MM-DD
          Transcriptions: counts.transcriptions,
          Generations: counts.generations,
        }))
    : [];

  const modelChartData = modelData
    ? Object.entries(modelData.models).map(([model, stats]) => ({
        model: model.replace('claude-', 'c-').replace('gpt-', 'g-'),
        fullName: model,
        Total: stats.total,
        Errors: stats.errors,
        'Avg Latency (s)': Math.round(stats.avg_latency_ms / 100) / 10,
      }))
    : [];

  const StatusBadge = ({ status }: { status: string }) => {
    const colors: Record<string, string> = {
      healthy: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      degraded: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      down: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      unhealthy: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || colors.down}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-surface-950">
      {/* Header */}
      <header className="bg-white dark:bg-surface-900 border-b border-gray-200 dark:border-surface-800 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/app')}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-surface-800"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="flex items-center gap-2">
              <BarChart3 size={20} className="text-primary-600" />
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Admin Analytics</h1>
            </div>
            {/* Tabs */}
            <div className="flex items-center gap-1 ml-6">
              <button
                onClick={() => setActiveTab('overview')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'overview'
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-800'
                }`}
              >
                <Activity size={15} />
                Overview
              </button>
              <button
                onClick={() => setActiveTab('customers')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'customers'
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-800'
                }`}
              >
                <Users size={15} />
                Customers
              </button>
              <button
                onClick={() => setActiveTab('billing')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'billing'
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-800'
                }`}
              >
                <CreditCard size={15} />
                Billing
              </button>
              <button
                onClick={() => setActiveTab('costs')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'costs'
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-800'
                }`}
              >
                <DollarSign size={15} />
                API Costs
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'history'
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-800'
                }`}
              >
                <History size={15} />
                Usage History
              </button>
              <button
                onClick={() => setActiveTab('edits')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'edits'
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-800'
                }`}
              >
                <GitCompare size={15} />
                Edit Tracking
              </button>
              <button
                onClick={() => setActiveTab('classification')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'classification'
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-800'
                }`}
              >
                <Brain size={15} />
                Classification
              </button>
              <button
                onClick={() => setActiveTab('learning')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'learning'
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-800'
                }`}
              >
                <GraduationCap size={15} />
                Learning
              </button>
              <button
                onClick={() => setActiveTab('feedback')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'feedback'
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-800'
                }`}
              >
                <MessageSquare size={15} />
                Feedback
              </button>
              <button
                onClick={() => setActiveTab('workshop')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'workshop'
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-800'
                }`}
              >
                <Hammer size={15} />
                Workshop
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Page guide — one explainer per tab, same static-page pattern
                as /build-history.html. Opens in a new browser tab so the
                dashboard state is untouched. */}
            <a
              href={`/help/admin-${activeTab}.html`}
              target="_blank"
              rel="noreferrer"
              title="How to read this page"
              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-surface-800 transition-colors"
            >
              <HelpCircle size={18} />
            </a>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="text-sm border border-gray-300 dark:border-surface-700 rounded-md px-2 py-1.5 bg-white dark:bg-surface-800 text-gray-700 dark:text-gray-200"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              onClick={fetchAll}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-md"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <span className="text-xs text-gray-400">{user?.full_name}</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Customers Tab */}
        {activeTab === 'customers' && <Customers days={days} />}

        {/* Billing Tab */}
        {activeTab === 'billing' && <Billing />}

        {/* History Tab */}
        {activeTab === 'costs' && <CostAnalytics days={days} />}

        {activeTab === 'history' && <UsageHistory days={days} />}

        {/* Edit Tracking Tab */}
        {activeTab === 'edits' && <EditHistory days={days} />}

        {/* Classification Tab */}
        {activeTab === 'classification' && <ClassificationInsights days={days} />}

        {/* Learning Engine Tab */}
        {activeTab === 'learning' && <Learning days={days} />}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <div style={{ height: 'calc(100vh - 180px)' }}>
            <FeedbackInbox />
          </div>
        )}

        {/* Workshop Tab */}
        {activeTab === 'workshop' && (
          <div style={{ height: 'calc(100vh - 180px)' }}>
            <Workshop />
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && <>
        {/* Service Health */}
        {health && (
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500 uppercase tracking-wide">System</span>
                <StatusBadge status={health.status} />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">v{health.version}</p>
            </div>
            <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500 uppercase tracking-wide">Database</span>
                <StatusBadge status={health.services.database.status} />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">PostgreSQL</p>
            </div>
            <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500 uppercase tracking-wide">Transcription</span>
                {health.services.transcription ? (
                  <StatusBadge status={health.services.transcription.backend === 'openai_api' ? 'healthy' : (health.services.whisper?.status || 'unknown')} />
                ) : health.services.whisper ? (
                  <StatusBadge status={health.services.whisper.status} />
                ) : (
                  <StatusBadge status="unknown" />
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {health.services.transcription
                  ? health.services.transcription.backend === 'openai_api' ? 'OpenAI Whisper API' : 'Local Whisper'
                  : `${health.services.whisper?.model || '—'} / ${health.services.whisper?.device || '—'}`}
              </p>
            </div>
            <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500 uppercase tracking-wide">LLM</span>
                {health.services.llm.anthropic_configured && health.services.llm.openai_configured ? (
                  <StatusBadge status="healthy" />
                ) : (
                  <StatusBadge status="degraded" />
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{health.services.llm.default_model}</p>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard
              icon={<Mic size={20} />}
              label="Transcriptions"
              value={summary.transcriptions.total}
              sub={`${summary.transcriptions.errors} errors (${summary.transcriptions.error_rate}%)`}
              color="blue"
            />
            <SummaryCard
              icon={<FileText size={20} />}
              label="Generations"
              value={summary.generations.total}
              sub={`${summary.generations.errors} errors (${summary.generations.error_rate}%)`}
              color="teal"
            />
            <SummaryCard
              icon={<Clock size={20} />}
              label="Avg Transcription"
              value={`${(summary.transcriptions.latency.avg_ms / 1000).toFixed(1)}s`}
              sub={`p95: ${(summary.transcriptions.latency.p95_ms / 1000).toFixed(1)}s`}
              color="purple"
            />
            <SummaryCard
              icon={<Cpu size={20} />}
              label="Avg Generation"
              value={`${(summary.generations.latency.avg_ms / 1000).toFixed(1)}s`}
              sub={`p95: ${(summary.generations.latency.p95_ms / 1000).toFixed(1)}s`}
              color="orange"
            />
          </div>
        )}

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Usage Chart */}
          <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-4">Daily Usage</h3>
            {dailyChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="Transcriptions" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Generations" stroke="#14b8a6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-400 text-sm">No data yet</div>
            )}
          </div>

          {/* Model Usage Chart */}
          <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-4">Usage by Model</h3>
            {modelChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={modelChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="model" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value, name) => [value as number, String(name)]}
                    labelFormatter={(label) => {
                      const item = modelChartData.find((d) => d.model === label);
                      return item?.fullName || label;
                    }}
                  />
                  <Legend />
                  <Bar dataKey="Total" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Errors" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-400 text-sm">No data yet</div>
            )}
          </div>
        </div>

        {/* Recent Logs */}
        <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-surface-800 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Recent Logs</h3>
            <div className="flex items-center gap-2">
              <select
                value={logFilter}
                onChange={(e) => setLogFilter(e.target.value)}
                className="text-xs border border-gray-300 dark:border-surface-700 rounded px-2 py-1 bg-white dark:bg-surface-800 text-gray-700 dark:text-gray-200"
              >
                <option value="">All events</option>
                <option value="transcription">Transcriptions</option>
                <option value="generation">Generations</option>
              </select>
            </div>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-surface-800 max-h-[500px] overflow-y-auto">
            {logs.length === 0 && (
              <div className="px-4 py-8 text-center text-gray-400 text-sm">No logs yet. Start dictating to see data here.</div>
            )}
            {logs.map((log) => (
              <div key={log.id} className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-surface-800/50">
                <div
                  className="flex items-center gap-3 cursor-pointer"
                  onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                >
                  {expandedLog === log.id ? (
                    <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
                  )}
                  {log.status === 'success' ? (
                    <CheckCircle2 size={14} className="text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle size={14} className="text-red-500 flex-shrink-0" />
                  )}
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                      log.event_type === 'transcription'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400'
                    }`}
                  >
                    {log.event_type}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate flex-1">
                    {log.transcription_text?.slice(0, 80) || log.error_message || '—'}
                    {(log.transcription_text?.length || 0) > 80 ? '...' : ''}
                  </span>
                  {log.model_used && (
                    <span className="text-xs text-gray-400 flex-shrink-0">{log.model_used}</span>
                  )}
                  <span className="text-xs text-gray-400 flex-shrink-0 w-16 text-right">
                    {log.latency_ms ? `${(log.latency_ms / 1000).toFixed(1)}s` : '—'}
                  </span>
                  <span className="text-xs text-gray-400 flex-shrink-0 w-28 text-right">
                    {log.created_at ? new Date(log.created_at).toLocaleString('en-AU', { hour12: false, month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
                {expandedLog === log.id && (
                  <div className="mt-2 ml-7 space-y-2 text-xs">
                    {log.transcription_text && (
                      <div>
                        <span className="font-medium text-gray-500 dark:text-gray-400">Transcription:</span>
                        <p className="mt-0.5 text-gray-700 dark:text-gray-300 whitespace-pre-wrap bg-gray-50 dark:bg-surface-800 rounded p-2">
                          {log.transcription_text}
                        </p>
                      </div>
                    )}
                    {log.ai_response_text && (
                      <div>
                        <span className="font-medium text-gray-500 dark:text-gray-400">AI Response:</span>
                        <p className="mt-0.5 text-gray-700 dark:text-gray-300 whitespace-pre-wrap bg-gray-50 dark:bg-surface-800 rounded p-2 max-h-64 overflow-y-auto">
                          {log.ai_response_text}
                        </p>
                      </div>
                    )}
                    {log.error_message && (
                      <div>
                        <span className="font-medium text-red-500">Error:</span>
                        <p className="mt-0.5 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded p-2">
                          {log.error_message}
                        </p>
                      </div>
                    )}
                    <div className="flex gap-4 text-gray-400">
                      {log.exam_category && <span>Category: {log.exam_category}</span>}
                      {log.whisper_language && <span>Language: {log.whisper_language}</span>}
                      {log.audio_duration_seconds != null && <span>Audio: {log.audio_duration_seconds.toFixed(1)}s</span>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        </>}
      </div>
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  sub,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub: string;
  color: string;
}) {
  const colors: Record<string, string> = {
    blue: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400',
    teal: 'text-teal-600 bg-teal-50 dark:bg-teal-900/20 dark:text-teal-400',
    purple: 'text-purple-600 bg-purple-50 dark:bg-purple-900/20 dark:text-purple-400',
    orange: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400',
  };

  return (
    <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>{icon}</div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{value}</p>
          <p className="text-xs text-gray-400">{sub}</p>
        </div>
      </div>
    </div>
  );
}
